package top.wcpe.allincore.ui.runtime.render

import top.wcpe.allincore.ui.runtime.render.UiRenderBridge
import top.wcpe.allincore.ui.runtime.render.model.GradientType
import top.wcpe.allincore.ui.runtime.render.model.GuiBlendMode
import top.wcpe.allincore.ui.runtime.render.model.RenderBatch
import top.wcpe.allincore.ui.runtime.render.model.RenderCommand
import top.wcpe.allincore.ui.runtime.render.model.RenderCommandType
import top.wcpe.allincore.ui.runtime.render.model.ShadowParams
import top.wcpe.allincore.ui.runtime.render.model.StyledFill
import top.wcpe.allincore.ui.runtime.style.Brush
import top.wcpe.allincore.ui.runtime.style.CornerRadii
import top.wcpe.allincore.ui.runtime.style.Mask
import top.wcpe.allincore.ui.runtime.style.ShadowSpec
import top.wcpe.allincore.ui.runtime.style.VisualModifier
import top.wcpe.allincore.ui.runtime.material.MaterialInteractionState
import top.wcpe.allincore.ui.runtime.material.TextureLayerRecipe

/**
 * [RenderSink] 的平台中立实现：把视觉 modifier 翻译成 [UiRenderBridge] 调用
 * （submitBatch / applyNeonGlow / fillRect），提交范式与 BoxRenderer 一致，但由 [ModifierDispatcher]
 * 驱动、不依赖 StyleVisual 上帝类。本类零 Minecraft 依赖、可单测（配合假 UiRenderBridge）。
 */
@Suppress("LongParameterList")
class RenderBridgeSink(private val bridge: UiRenderBridge) : RenderSink {

    override fun surface(
        x: Float,
        y: Float,
        w: Float,
        h: Float,
        brush: Brush,
        corner: CornerRadii,
        borderWidth: Float,
        borderColor: Int,
        mask: Mask?,
    ) {
        // 纯色 + 无圆角 + 无边框 + 无遮罩 → 走快速 fillRect（与 BoxRenderer 零回归路径一致）。
        // 有遮罩须走 SDF shader，不能用 fillRect 近似。
        val activeMask = mask?.takeIf { it.isActive() }
        val noShapeNoMask = corner.isZero() && borderWidth <= 0f && activeMask == null
        if (brush is Brush.Solid && noShapeNoMask) {
            bridge.fillRect(x, y, w, h, brush.color)
            return
        }
        val builder = StyledFill.builder()
            .cornerRadii(corner.toArray())
            .borderWidth(borderWidth)
            .borderColor(borderColor)
        if (activeMask != null) {
            builder.maskType(activeMask.type)
                .maskParam1(activeMask.param1)
                .maskParam2(activeMask.param2)
                .maskParam3(activeMask.param3)
        }
        if (brush is Brush.Solid) {
            val cmd = RenderCommand.roundedRect(x, y, w, h, brush.color, RenderCommand.NO_CLIP, builder.build())
            bridge.submitBatch(RenderBatch(RenderCommandType.ROUNDED_RECT, null, RenderCommand.NO_CLIP, listOf(cmd)))
            return
        }
        applyGradient(builder, brush)
        // 渐变命令的 argb 基色由 stops 决定，此处传 0。
        val cmd = RenderCommand.gradient(x, y, w, h, 0, RenderCommand.NO_CLIP, builder.build())
        bridge.submitBatch(RenderBatch(RenderCommandType.GRADIENT, null, RenderCommand.NO_CLIP, listOf(cmd)))
    }

    /** 把渐变 [Brush]（Linear / Radial）写入 fill builder；Solid 在调用前已短路。 */
    private fun applyGradient(builder: StyledFill.Builder, brush: Brush) {
        when (brush) {
            is Brush.Solid -> Unit
            is Brush.Linear ->
                builder.gradientType(GradientType.LINEAR)
                    .gradientAngleRadians(brush.angleRadians)
                    .gradientStops(brush.stops)

            is Brush.Radial ->
                builder.gradientType(GradientType.RADIAL)
                    .gradientStops(brush.stops)
                    .radialCenterX(brush.centerX)
                    .radialCenterY(brush.centerY)
                    .radialRadius(brush.radius)
        }
    }

    override fun shadow(x: Float, y: Float, w: Float, h: Float, spec: ShadowSpec, corner: CornerRadii) {
        // ShadowParams 字段序为 (offsetX, offsetY, blur, color, spread, inset)，按名取值不依赖位置。
        val params = ShadowParams(spec.offsetX, spec.offsetY, spec.blur, spec.color, spec.spread, spec.inset)
        // 附带宿主圆角让阴影 SDF 与圆角对齐（与 BoxRenderer.submitOneShadow 一致）。
        val fill = StyledFill.builder().cornerRadii(corner.toArray()).build()
        val cmd = RenderCommand.shadow(x, y, w, h, RenderCommand.NO_CLIP, params, fill)
        bridge.submitBatch(RenderBatch(RenderCommandType.SHADOW, null, RenderCommand.NO_CLIP, listOf(cmd)))
    }

    override fun neon(
        x: Float,
        y: Float,
        w: Float,
        h: Float,
        color: Int,
        intensity: Float,
        size: Float,
        animated: Boolean,
        corner: CornerRadii,
    ) {
        bridge.applyNeonGlow(x, y, w, h, color, intensity, size, animated, corner.toArray())
    }

    override fun ripple(
        x: Float,
        y: Float,
        w: Float,
        h: Float,
        color: Int,
        progress: Float,
        radius: Float,
        centerX: Float,
        centerY: Float,
        corner: CornerRadii,
    ) {
        bridge.applyRippleEffect(x, y, w, h, color, progress, radius, centerX, centerY, corner.toArray())
    }

    override fun flow(
        x: Float,
        y: Float,
        w: Float,
        h: Float,
        color: Int,
        speedHz: Float,
        widthPx: Float,
        angleDeg: Float,
        mode: Int,
        corner: CornerRadii,
    ) {
        bridge.applyFlow(x, y, w, h, color, speedHz, widthPx, angleDeg, mode, corner.toArray())
    }

    override fun energy(
        x: Float,
        y: Float,
        w: Float,
        h: Float,
        color: Int,
        intensity: Float,
        frequency: Float,
        speed: Float,
        pattern: Int,
        corner: CornerRadii,
    ) {
        bridge.applyEnergy(x, y, w, h, color, intensity, frequency, speed, pattern, corner.toArray())
    }

    override fun proceduralTexture(
        x: Float,
        y: Float,
        w: Float,
        h: Float,
        kind: String,
        colorA: Int,
        colorB: Int,
        scale: Float,
        density: Float,
        intensity: Float,
        corner: CornerRadii,
    ) {
        bridge.applyProceduralTexture(x, y, w, h, kind, colorA, colorB, scale, density, intensity, corner.toArray())
    }

    override fun backdropBlur(x: Float, y: Float, w: Float, h: Float, blurRadius: Float) {
        bridge.applyBackdropBlur(blurRadius, x, y, w, h)
    }

    override fun materialLayer(
        x: Float,
        y: Float,
        w: Float,
        h: Float,
        layer: VisualModifier.MaterialLayer,
        states: Set<MaterialInteractionState>,
    ) {
        val fields = layer.toBridgeFields().toMutableMap()
        // 交互态以布尔标志随 fields 透传给平台（FR-UI-024：只走 runtime，不进静态缓存 key）。
        if (states.isNotEmpty()) {
            fields["_hovered"] = MaterialInteractionState.HOVERED in states
            fields["_pressed"] = MaterialInteractionState.PRESSED in states
            fields["_focused"] = MaterialInteractionState.FOCUSED in states
            fields["_disabled"] = MaterialInteractionState.DISABLED in states
            fields["_selected"] = MaterialInteractionState.SELECTED in states
            fields["_active"] = MaterialInteractionState.ACTIVE in states
        }
        bridge.drawMaterialLayer(layer.kind, fields, x, y, w, h)
    }

    override fun particleLayer(
        x: Float,
        y: Float,
        w: Float,
        h: Float,
        elementId: String,
        ordinal: Int,
        layer: VisualModifier.ParticleLayer,
        states: Set<MaterialInteractionState>,
        mouseX: Float,
        mouseY: Float,
    ) {
        // 粒子静态参数 + 随帧变的鼠标坐标 / 交互态标志打进 spec 快照（后二者不进任何静态缓存 key）。
        val spec = mutableMapOf<String, Any?>(
            "kind" to layer.kind,
            "emission" to layer.emission,
            "layer" to layer.layer,
            "rate" to layer.rate,
            "count" to layer.count,
            "trigger" to layer.trigger,
            "lifetime" to layer.lifetime,
            "gravity" to layer.gravity,
            "wind" to layer.wind,
            "speed" to layer.speed,
            "size" to layer.size,
            "colorA" to layer.colorA,
            "colorB" to layer.colorB,
            "glow" to layer.glow,
            "bounce" to layer.bounce,
            "hoverForce" to layer.hoverForce,
            "opacity" to layer.opacity,
            "blend" to layer.blend,
            "seed" to layer.seed,
            "emitShape" to layer.emitShape,
            "emitEdge" to layer.emitEdge,
            "emitAngle" to layer.emitAngle,
            "emitSpread" to layer.emitSpread,
            "emitInward" to layer.emitInward,
            "noiseStrength" to layer.noiseStrength,
            "noiseFrequency" to layer.noiseFrequency,
            "attractX" to layer.attractX,
            "attractY" to layer.attractY,
            "attractStrength" to layer.attractStrength,
            "drag" to layer.drag,
            "colorOverLifetime" to layer.colorOverLifetime,
            "sizeOverLifetime" to layer.sizeOverLifetime,
            "alphaOverLifetime" to layer.alphaOverLifetime,
            "rotationOverLifetime" to layer.rotationOverLifetime,
            "texture" to layer.texture,
            "sheetRows" to layer.sheetRows,
            "sheetCols" to layer.sheetCols,
            "sheetFps" to layer.sheetFps,
            "trailLength" to layer.trailLength,
            "trailFade" to layer.trailFade,
            "_mouseX" to mouseX,
            "_mouseY" to mouseY,
            "_hovered" to (MaterialInteractionState.HOVERED in states),
            "_pressed" to (MaterialInteractionState.PRESSED in states),
            "_focused" to (MaterialInteractionState.FOCUSED in states),
        )
        bridge.drawParticleLayer(elementId, ordinal, spec, x, y, w, h)
    }

    private fun VisualModifier.MaterialLayer.toBridgeFields(): Map<String, Any?> = when (this) {
        is VisualModifier.TextureLayer -> TextureLayerRecipe.fromModifier(this).toBridgeFields()
        is VisualModifier.MaterialOverlay -> mapOf(
            "material" to material,
            "seed" to seed,
            "scale" to scale,
            "roughness" to roughness,
            "tint" to tint,
            "opacity" to opacity,
            "blend" to blend,
        )
        is VisualModifier.HistoryLayer -> mapOf(
            "source" to source, "frames" to frames, "downsample" to downsample, "decay" to decay,
            "blur" to blur, "distortion" to distortion, "opacity" to opacity, "blend" to blend,
            "trigger" to trigger,
        )
        is VisualModifier.WornFrame -> mapOf(
            "thickness" to thickness,
            "erosion" to erosion,
            "roughness" to roughness,
            "edgeStrength" to edgeStrength,
            "tint" to tint,
            "opacity" to opacity,
            "blend" to blend,
        )
        is VisualModifier.RuneOverlay -> mapOf(
            "runeSet" to runeSet,
            "seed" to seed,
            "glow" to glow,
            "density" to density,
            "tint" to tint,
            "opacity" to opacity,
            "blend" to blend,
        )
        is VisualModifier.MaterialGraph -> mapOf("graph" to graph, "budget" to budget, "fallback" to fallback)
        is VisualModifier.ShaderLayer -> mapOf(
            "program" to program,
            "params" to params,
            "opacity" to opacity,
            "blend" to blend,
            "fallback" to fallback,
        )
    }

    // ===== 作用域 / 包裹型效果（ADR-0047）：转发到 UiRenderBridge 的成对 begin/end 桥调用 =====

    override fun beginBlur(x: Float, y: Float, w: Float, h: Float, blurRadius: Float) {
        bridge.beginFilterGroup(blurRadius, x, y, w, h)
    }

    override fun endBlur() {
        bridge.endFilterGroup()
    }

    override fun beginHistoryCapture(
        x: Float,
        y: Float,
        w: Float,
        h: Float,
        elementId: String,
        layer: VisualModifier.HistoryLayer,
    ) {
        // 历史层参数以字段快照透传给平台；elementId 作为环形缓冲键之一。
        bridge.beginHistoryCapture(elementId, layer.toBridgeFields(), x, y, w, h)
    }

    override fun endHistoryCapture() {
        bridge.endHistoryCapture()
    }


    override fun pushBlendMode(mode: GuiBlendMode) {
        bridge.pushBlendMode(mode.ordinal)
    }

    override fun popBlendMode() {
        bridge.popBlendMode()
    }

    override fun pushColorFilter(matrix4x5: FloatArray) {
        bridge.pushColorMatrix(matrix4x5)
    }

    override fun popColorFilter() {
        bridge.popColorMatrix()
    }
}
